import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { AIRBNB_URL } from "@/data/property";
import { Reveal, MaskLineInView } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", check_in: "", check_out: "", group_size: "", purpose: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry sent! We'll get back to you within the hour.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or message us on Airbnb.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#F1E8DA] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div className="lg:sticky lg:top-28">
          <span className="text-sm uppercase tracking-widest text-[#1FB6C9] font-body">Get in Touch</span>
          <h2 className="mt-3 font-display font-semibold text-[#0E2A47] text-4xl md:text-6xl tracking-tight leading-[0.95]">
            <MaskLineInView>Plan your</MaskLineInView>
            <MaskLineInView delay={0.1}>
              <span className="font-serif-accent italic font-normal text-[#1FB6C9]">gathering.</span>
            </MaskLineInView>
          </h2>
          <p className="mt-6 text-[#0E2A47]/70 font-body text-lg max-w-md">
            Share a few details about your trip and we'll help you plan the perfect lakeside stay.
            Prefer Airbnb? Message us directly anytime.
          </p>
          <a
            href={AIRBNB_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-airbnb-message-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#0E2A47]/20 bg-white px-7 py-3.5 font-display font-medium text-[#0E2A47] transition-all duration-300 hover:border-[#1FB6C9] hover:text-[#1FB6C9]"
          >
            <MessageCircle size={18} /> Message the Host
          </a>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="inquiry-form"
            className="bg-[#FAFAF8] rounded-2xl p-7 md:p-9 border border-[#0E2A47]/5 shadow-xl shadow-[#0E2A47]/5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name" id="name">
                <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your name" className="bg-white" />
              </Field>
              <Field label="Email" id="email">
                <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-white" />
              </Field>
              <Field label="Check-in" id="check_in">
                <Input data-testid="input-checkin" type="date" value={form.check_in} onChange={set("check_in")} className="bg-white" />
              </Field>
              <Field label="Check-out" id="check_out">
                <Input data-testid="input-checkout" type="date" value={form.check_out} onChange={set("check_out")} className="bg-white" />
              </Field>
              <Field label="Group size" id="group_size">
                <Input data-testid="input-group" value={form.group_size} onChange={set("group_size")} placeholder="e.g. 18 guests" className="bg-white" />
              </Field>
              <Field label="Purpose of stay" id="purpose">
                <Input data-testid="input-purpose" value={form.purpose} onChange={set("purpose")} placeholder="Wedding, retreat, celebration…" className="bg-white" />
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Message" id="message">
                <Textarea data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us about your trip…" rows={4} className="bg-white" />
              </Field>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="inquiry-submit-btn"
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0E2A47] px-8 py-4 font-display font-medium text-white transition-all duration-300 hover:bg-[#1FB6C9] disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Inquiry"}
              <Send size={17} className="cta-arrow" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

const Field = ({ label, id, children }) => (
  <div>
    <Label htmlFor={id} className="text-sm font-body text-[#0E2A47]/70 mb-1.5 block">{label}</Label>
    {children}
  </div>
);
